import { ClassifierData } from 'types/classifiers';
import { CustomFieldDto } from 'types/customFields';
import { Employee, EmployeeCreateDto, EmployeeForm, EmployeeUpdateDto } from 'types/employees';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { getOptionsFromClassifier } from 'utils/formUtil';
import { mapEmploymentDtoToForm } from './util';

/** The classifiers {@link employeeDtoToForm} reads. */
export const EMPLOYEE_FORM_CLASSIFIERS = ['GENDER_TYPE', 'PERSON_TITLE', 'LANGUAGE', 'PARTNERSHIP_PEER_GROUP_TYPE'];

interface DtoToFormContext {
    classifiers: Record<string, ClassifierData>;
    /** The custom fields' form values, as `useCustomFields` builds them from the employee's. */
    customFieldValues: Record<string, unknown>;
}

/** The edit form's values for a fetched employee. */
export const employeeDtoToForm = (
    employee: Employee,
    { classifiers, customFieldValues }: DtoToFormContext
): EmployeeForm => ({
    cognitoId: employee.cognitoId,
    firstName: employee.firstName ?? '',
    lastName: employee.lastName ?? '',
    localFirstName: employee.localFirstName ?? '',
    localLastName: employee.localLastName ?? '',
    personTitle:
        getOptionsFromClassifier<string, string>(classifiers['PERSON_TITLE']).find(
            (val) => val.value === employee.personTitleCode
        ) ?? null,
    isActive: employee.isActive ?? true,
    personalIdCode: employee.personalIdCode ?? '',
    workEmailAddress: employee.workEmailAddress ?? '',
    workPhone: employee.workPhone ?? '',
    mobilePhone: employee.mobilePhone ?? '',
    notes: employee.notes ?? '',
    previousNames: employee.previousNames ?? '',
    manager: employee.manager?.id
        ? {
              id: employee.manager.id,
              name: employee.manager.name,
              firstName: employee.manager.firstName,
              lastName: employee.manager.lastName,
          }
        : null,
    employmentHistory: employee.employmentHistory?.map(mapEmploymentDtoToForm) ?? [],
    affiliations: employee.affiliations.map((it) => ({ ...it, error: false })),
    systemRoles: (employee.systemRoles ?? []).map((val) => ({ id: val.id, name: val.name })),
    coreTeam: employee.coreTeam?.id ? { id: employee.coreTeam.id, name: employee.coreTeam.name } : null,
    supportTeams: employee.supportTeams ?? [],
    chairmanTeams: employee.chairmanTeams ?? [],
    practiceAreas: employee.practiceAreas ?? [],
    languagesSpoken:
        getOptionsFromClassifier<string, string>(classifiers['LANGUAGE']).filter((val) =>
            employee.languagesSpoken.some((language) => language == val.value)
        ) ?? [],
    taskReceivers: employee.taskReceivers ?? [],
    receivingTasksForEmployees: employee.receivingTasksForEmployees ?? [],
    canImpersonate: employee.canImpersonate ?? [],
    employmentStartDate: employee.employmentStartDate
        ? formatDateStringInput(new Date(employee.employmentStartDate))
        : null,
    employmentLeaveDate: employee.employmentLeaveDate
        ? formatDateStringInput(new Date(employee.employmentLeaveDate))
        : null,
    birthdayDate: employee.birthdayDate ? formatDateStringInput(new Date(employee.birthdayDate)) : null,
    sensitiveInfo: employee.sensitiveInfo ?? '',
    isPoliticallyExposedPerson: employee.isPoliticallyExposedPerson ?? false,
    personalAddress: employee.personalAddress ?? null,
    annualTargetHours: employee.annualTargetHours ?? null,
    plannedWorkdaysOff: employee.plannedWorkdaysOff ?? null,
    profilePictureName: employee.profilePictureName ?? null,
    signaturePictureName: employee.signaturePictureName ?? null,
    vatRegNum: employee.vatRegNum ?? '',
    genderType:
        getOptionsFromClassifier<string, string>(classifiers['GENDER_TYPE']).find(
            (val) => val.value === employee.genderTypeCode
        ) ?? null,
    partnershipPeerGroup:
        getOptionsFromClassifier<string, string>(classifiers['PARTNERSHIP_PEER_GROUP_TYPE']).find(
            (val) => val.value === employee.partnershipPeerGroupTypeCode
        ) ?? null,
    overwriteSignedByEmployeeId: employee.overwriteSignedByEmployeeId ?? null,
    web: employee.web
        ? {
              publicizePerson: employee.web.publicizePerson,
              publicizeMobile: employee.web.publicizeMobile,
              onLeave: employee.web.onLeave,
          }
        : null,
    webPictureBase64: null,
    ...customFieldValues,
});

/** The create request for the form's values. */
export const employeeFormToRequest = (form: EmployeeForm, customFields: CustomFieldDto[]): EmployeeCreateDto => {
    const supportTeams = form.supportTeams.map((val) => ({
        id: val.id,
        name: val.name,
        teamMembershipType: 'SUPPORT',
    }));
    const chairmanTeams = form.chairmanTeams.map((val) => ({
        id: val.id,
        name: val.name,
        teamMembershipType: 'CHAIRMAN',
    }));
    return {
        firstName: form.firstName,
        lastName: form.lastName,
        localFirstName: form.localFirstName || null,
        localLastName: form.localLastName || null,
        personTitleCode: form.personTitle ? form.personTitle.value : null,
        isActive: form.isActive,
        personalIdCode: form.personalIdCode,
        workEmailAddress: form.workEmailAddress,
        workPhone: form.workPhone,
        mobilePhone: form.mobilePhone,
        notes: form.notes,
        previousNames: form.previousNames,
        employmentStartDate: form?.employmentStartDate ? new Date(form.employmentStartDate) : null,
        employmentLeaveDate: form?.employmentLeaveDate ? new Date(form.employmentLeaveDate) : null,
        birthdayDate: form?.birthdayDate ? new Date(form.birthdayDate) : null,
        managerEmployeeId: form?.manager?.id ?? null,
        employmentHistory: [
            ...(form.employmentHistory?.map((employment) => ({
                recordId: employment.recordId,
                jobRoleId: employment.jobRole?.id || null,
                jobPositionId: employment.jobPosition?.id || null,
                validFrom: employment.validFrom ? new Date(employment.validFrom).getTime() : null,
                validTo: employment.validTo ? new Date(employment.validTo).getTime() : null,
                officeId: employment.office?.id || null,
                coreTeamId: employment.coreTeam?.id || null,
            })) || []),
        ],
        affiliations: form.affiliations.map(({ partyEmployeeId, party, notes }) => ({
            partyEmployeeId,
            party: party!,
            notes: notes!,
        })),
        systemRoles: form?.systemRoles?.map((val) => ({ id: val.id, name: val.name })) ?? null,
        coreTeam: form?.coreTeam?.id ? { id: form.coreTeam.id, name: form.coreTeam.name } : null,
        employeeTeams: [...supportTeams, ...chairmanTeams],
        practiceAreas: form.practiceAreas?.map((val) => ({ id: val.id, name: val.name })) ?? null,
        languagesSpoken: form.languagesSpoken?.map((val) => val.value) ?? [],
        taskReceivers: form.taskReceivers?.map((val) => ({ id: val.id, name: val.name })) ?? null,
        canImpersonate: form.canImpersonate?.map((val) => ({ id: val.id, name: val.name })) ?? null,
        profilePictureName: form.profilePictureName || null,
        signaturePictureName: form.signaturePictureName || null,
        vatRegNum: form.vatRegNum || null,
        genderTypeCode: form.genderType ? form.genderType.value : null,
        partnershipPeerGroupTypeCode: form.partnershipPeerGroup ? form.partnershipPeerGroup.value : null,
        overwriteSignedByEmployeeId: form.overwriteSignedByEmployeeId?.id ?? null,
        sensitiveInfo: form.sensitiveInfo || null,
        isPoliticallyExposedPerson: form.isPoliticallyExposedPerson,
        personalAddress: form.personalAddress || null,
        ...(customFields.length > 0 ? { customFields: customFields } : {}),
    };
};

/** The update request: the create one, with the targets. */
export const employeeFormToUpdateRequest = (
    form: EmployeeForm,
    customFields: CustomFieldDto[],
    id: string
): EmployeeUpdateDto => ({
    ...employeeFormToRequest(form, customFields),
    id,
    // Sent back even while the subsection is hidden, so an edit elsewhere never clears them.
    annualTargetHours: form.annualTargetHours ?? null,
    plannedWorkdaysOff: form.plannedWorkdaysOff ?? null,
    ...(form.web ? { web: form.web } : {}),
});

/** The form fields each card of the employee page edits, in the cards' order. */
export const EMPLOYEE_SECTION_FIELDS = {
    identity: [
        'profilePictureName',
        'signaturePictureName',
        'firstName',
        'localFirstName',
        'lastName',
        'localLastName',
        'personTitle',
        'previousNames',
    ],
    account: ['isActive', 'systemRoles', 'canImpersonate'],
    'contact-details': ['workEmailAddress', 'mobilePhone', 'workPhone'],
    employment: [
        'manager',
        'vatRegNum',
        'overwriteSignedByEmployeeId',
        'employmentStartDate',
        'employmentLeaveDate',
        'notes',
        'taskReceivers',
    ],
    personal: [
        'genderType',
        'personalIdCode',
        'birthdayDate',
        'sensitiveInfo',
        'isPoliticallyExposedPerson',
        'personalAddress',
    ],
    targets: ['annualTargetHours', 'plannedWorkdaysOff'],
    competence: ['supportTeams', 'chairmanTeams', 'practiceAreas', 'languagesSpoken'],
    'peer-group': ['partnershipPeerGroup'],
    'employment-history': ['employmentHistory'],
    affiliations: ['affiliations'],
    'custom-fields': [],
    'web-integration': ['web'],
} satisfies Record<string, (keyof EmployeeForm)[]>;

export type EmployeeSectionId = keyof typeof EMPLOYEE_SECTION_FIELDS;

interface SectionGateContext {
    isSelf: boolean;
    hasUpdateEmployees: boolean;
    canUpdatePersonalData: boolean;
    canReadAllPersonalData: boolean;
    hasUpdatePartnerPeerGroupPermission: boolean;
}

/**
 * Which cards the user may edit, as the server applies an update: a caller without UPDATE.EMPLOYEES has only the
 * personal data saved, and that only with full read access to it, and the peer group takes its own privilege.
 * Your own targets keep their own modal.
 */
export const employeeSectionGates = ({
    isSelf,
    hasUpdateEmployees,
    canUpdatePersonalData,
    canReadAllPersonalData,
    hasUpdatePartnerPeerGroupPermission,
}: SectionGateContext): Record<EmployeeSectionId, boolean> => {
    const canEditPersonal = canUpdatePersonalData && canReadAllPersonalData;
    return {
        identity: hasUpdateEmployees,
        account: hasUpdateEmployees,
        'contact-details': hasUpdateEmployees,
        employment: hasUpdateEmployees,
        personal: canEditPersonal,
        targets: canEditPersonal && !isSelf,
        competence: hasUpdateEmployees,
        'peer-group': hasUpdatePartnerPeerGroupPermission,
        'employment-history': hasUpdateEmployees,
        affiliations: hasUpdateEmployees,
        'custom-fields': hasUpdateEmployees,
        'web-integration': hasUpdateEmployees,
    };
};

/** The card `/employee/:id/edit` lands on: the first one the user may edit among those shown, if any. */
export const firstEditableEmployeeCard = (
    gates: Record<EmployeeSectionId, boolean>,
    shownCards: Partial<Record<EmployeeSectionId, boolean>>
): EmployeeSectionId | undefined =>
    (Object.keys(gates) as EmployeeSectionId[]).find((card) => gates[card] && shownCards[card] !== false);
